import React, { useCallback, useEffect, useState } from "react";
import { Agendamento } from "../../../services/Agendamento";
import { formatDateToISO, reverseToIsoDate } from "../../../utils/dateTransform";
import {
  IAgendamentoDTO,
  IAgendamentoDaHoraDTO,
  IIniciarAgendamentoProps,
} from "../../../types/agendamento";

import { useContextSite } from "../../../context/Context";
import { toast } from "react-toastify";
import { StatusAgendamentoEnum } from "../../../enums/statusAgendamento";
import { Colaborador } from "../../../services/Colaborador";
import { IColaboradorCompletoDTO } from "../../../types/colaborador";
import { useMediaQuery } from "react-responsive";
import { ISelectOptions } from "../../../types/inputs";
import { TipoColaboradorEnum } from "../../../enums/tipoColaborador";
import { Loja } from "../../../services/Lojas";
import { useUserPermissions } from "../../../hooks/useUserPermissions";

interface IModalStartProps extends IIniciarAgendamentoProps {
  open: boolean;
}

type dataAgendamentoProps = {
  vagas: number;
  totalAgendamentos: number;
};

export const useStores = () => {
  const { setIsLoad } = useContextSite();
  const { isAdmin, hasRecurso } = useUserPermissions();
  const isMultiloja = isAdmin() || hasRecurso("MULTILOJA");

  const [selectedStoreUuid, setSelectedStoreUuid] = useState<string>();
  const [lojasOptions, setLojasOptions] = useState<ISelectOptions[]>([]);

  const [agendamentosEmEspera, setAgendamentosEmEspera] = useState<IAgendamentoDTO[]>([] as IAgendamentoDTO[]);
  const [agendamentos, setAgendamentos] = useState<IAgendamentoDaHoraDTO[]>([] as IAgendamentoDaHoraDTO[]);
  const [dataAgendamento, setDataAgendamento] = useState<dataAgendamentoProps>({} as dataAgendamentoProps);
  const [modalStart, setModalStart] = useState<IModalStartProps>({ open: false });
  const isMobile = useMediaQuery({ maxWidth: "500px" });
  const [colaborador, setColaborador] = useState<IColaboradorCompletoDTO>({} as IColaboradorCompletoDTO);
  const [vistoriadoresOptions, setVistoriadoresOptions] = useState<ISelectOptions[]>([]);
  const [baitasOptions, setBaiaOptions] = useState<ISelectOptions[]>([]);

  const uuidLoja = isMultiloja ? selectedStoreUuid : colaborador?.loja?.uuid;

  function transformData(data: IAgendamentoDaHoraDTO[]) {
    const result = data.flatMap((item) => item.agendamentos);
    return result.filter((item) => item.emEspera);
  }

  function iniciarVistoria(e: React.SyntheticEvent) {
    e.preventDefault();

    const PAYLOAD: IIniciarAgendamentoProps = {
      uuid: modalStart?.uuid,
      uuidBaia: modalStart?.uuidBaia,
      uuidVistoriador: modalStart?.uuidVistoriador,
    };

    setIsLoad(true);
    Agendamento.iniciar(PAYLOAD)
      .then(({ data }) => {
        toast.success("Agendamento iniciado");
        getData();
      })
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => {
          toast.error(mensagem);
        }
      )
      .finally(() => {
        setIsLoad(false);
        setModalStart({ open: false });
      });
  }

  function handleWait({ uuid }: { uuid: string }) {
    setIsLoad(true);
    Agendamento.colocarEmEspera({ uuidAgendamento: uuid })
      .then(({ data }) => {
        toast.success("Agendamento em espera!");
        getData();
      })
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => {
          toast.error(mensagem);
        }
      )
      .finally(() => {
        setIsLoad(false);
      });
  }

  const getData = useCallback(() => {
    if (!uuidLoja) return;
    setIsLoad(true);
    const hoje = formatDateToISO(new Date());

    Agendamento.getByHour({
      data: hoje,
      status: [
        StatusAgendamentoEnum.AGENDADO,
        StatusAgendamentoEnum.INICIADO,
        StatusAgendamentoEnum.FINALIZADO,
      ],
      uuidLoja: uuidLoja,
    })
      .then(({ data }) => {
        const emEspera = transformData(data.agendamentos);
        const foraDeEspera = data.agendamentos.filter((item) =>
          item.agendamentos.some((_) => !_.emEspera)
        );
        setAgendamentos(foraDeEspera);
        setAgendamentosEmEspera(emEspera);
        setDataAgendamento({
          totalAgendamentos: data.totalAgendamentos,
          vagas: data.vagas,
        });
      })
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => toast.error(mensagem)
      )
      .finally(() => setIsLoad(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuidLoja]);

  const getColaborador = useCallback(async () => {
    return Colaborador.atual().then(({ data }) => data);
  }, []);

  useEffect(() => {
    getColaborador()
      .then((data) => setColaborador(data))
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => {
          console.log(mensagem);
          // toast.error(mensagem);
        }
      );
  }, [getColaborador]);

  useEffect(() => {
    if (isMultiloja) {
      setIsLoad(true);
      Loja.get({ ativo: true })
        .then(({ data }) => {
          const options = data.content.map((item) => ({
            label: item.nome,
            value: item.uuid,
          }));
          setLojasOptions(options);
        })
        .catch(
          ({
            response: {
              data: { mensagem },
            },
          }) => toast.error(mensagem)
        )
        .finally(() => {
          setIsLoad(false);
        });
    }
  }, [isMultiloja, setIsLoad]);

  useEffect(() => {
    if (!isMultiloja) return;

    if (colaborador?.loja?.uuid) {
      setSelectedStoreUuid(colaborador.loja.uuid);
    } else if (lojasOptions.length > 0 && !selectedStoreUuid) {
      setSelectedStoreUuid(lojasOptions[0].value);
    }
  }, [colaborador?.loja?.uuid, lojasOptions, isMultiloja, selectedStoreUuid]);

  useEffect(() => {
    if (isMultiloja) {
      if (uuidLoja) {
        getData();
      }
    } else {
      if (colaborador?.uuid) {
        getData();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuidLoja, colaborador?.uuid]);

  useEffect(() => {
    if (modalStart?.open && uuidLoja) {
      Colaborador.listarPorLoja({
        tipo: TipoColaboradorEnum.VISTORIADOR,
        disponivel: true,
        uuidLoja: uuidLoja,
      }).then(({ data }) => {
        const options = data.map((item) => ({
          value: item.uuid,
          label: item.nome,
          element: item,
        }));

        setVistoriadoresOptions(options);
      });

      Loja.getBaiasLivres({ uuid: uuidLoja }).then(
        ({ data }) => {
          const options = data.map((item) => ({
            value: item.uuid,
            label: item.nome,
          }));
          setBaiaOptions(options);
        }
      );

      return;
    }
  }, [modalStart?.open, uuidLoja]);

  return {
    iniciarVistoria,
    agendamentos,
    agendamentosEmEspera,
    handleWait,
    modalStart,
    setModalStart,
    vistoriadoresOptions,
    baitasOptions,
    isMobile,
    dataAgendamento,
    isMultiloja,
    lojasOptions,
    selectedStoreUuid,
    setSelectedStoreUuid,
  };
};
